// Identity + authorization context for the signed-in CSD user.
// Sourced from the CSD API post-login (FDD §4; TDD Architecture §2.5).

/** CSD roles; the values are the role strings the API sends on each `/auth/me`
 *  authorized org. */
export enum UserRole {
  NabpAdmin = 'NABP_ADMIN',
  NabpStaff = 'NABP_STAFF',
  Poc = 'POC',
  AuthorizedPrivilegeUser = 'AUTHORIZED_PRIVILEGE_USER',
  AuthorizedNonPrivilegeUser = 'AUTHORIZED_NON_PRIVILEGE_USER',
}

/** Every role, in declaration order. */
export const USER_ROLES: readonly UserRole[] = Object.values(UserRole);

/** POC and NABP roles — every data type, Reports, and no per-org access restriction. */
export const PRIVILEGED_ROLES: readonly UserRole[] = [
  UserRole.Poc,
  UserRole.NabpAdmin,
  UserRole.NabpStaff,
];

export function isPrivileged(role: UserRole | null | undefined): boolean {
  return !!role && PRIVILEGED_ROLES.includes(role);
}

/** Authorized Users — access follows their assigned data types and locations. Both
 *  variants currently get the same dashboard access. */
export const AUTHORIZED_USER_ROLES: readonly UserRole[] = [
  UserRole.AuthorizedPrivilegeUser,
  UserRole.AuthorizedNonPrivilegeUser,
];

export function isAuthorizedUser(role: UserRole | null | undefined): boolean {
  return !!role && AUTHORIZED_USER_ROLES.includes(role);
}

/** Roles that may open Reports: POC / NABP, and Authorized Users with view/edit access.
 *  A view-only Authorized User has no Reports at all. */
export const REPORTS_ROLES: readonly UserRole[] = [
  ...PRIVILEGED_ROLES,
  UserRole.AuthorizedPrivilegeUser,
];

export function canAccessReports(role: UserRole | null | undefined): boolean {
  return !!role && REPORTS_ROLES.includes(role);
}

/** A non-privileged Authorized User can view Employee Monitoring / Prehire but take no
 *  action there — the exception and hiring-decision actions are disabled.
 *  Export to Excel stays available. */
export function isActionsReadOnly(role: UserRole | null | undefined): boolean {
  return role === UserRole.AuthorizedNonPrivilegeUser;
}

/** Data types a user can be assigned, as `/auth/me` sends them on each authorized org
 *  (`authorized_orgs[].data_types`). */
export enum DataPermission {
  EmployeeData = 'EMPLOYEE_DATA',
  PreHireData = 'PRE_HIRE_DATA',
}

export type ProfessionTab = 'PHARMACIST' | 'TECHNICIAN' | 'INTERN' | 'NURSE';

/** An organization the user can switch between (Organization selector). */
export interface OrganizationOption {
  id: string;
  name: string;
}

export interface UserContext {
  userId: string;
  displayName: string;
  role: UserRole;
  organizationId: string;
  organizationName: string;
  /** Organizations the user may switch to (backend-provided). */
  availableOrganizations?: OrganizationOption[];
  assignedDataTypes: DataPermission[];
  assignedLocationIds: string[];
  /** The active org has Location Management enabled. OFF hides the Location
   *  Management nav item and routes and the location parts of User Management. */
  locationManagementEnabled: boolean;
  /** An Authorized User with no data type in the active org, or no location when the
   *  org has Location Management enabled: the shell shows only their own Session Audit
   *  Log plus the access banner. Never set for POC / NABP roles. */
  accessRestricted: boolean;
  // Profession sub-tabs enabled by the client contract (drives Employee/Pre-Hire tabs).
  activeModules: ProfessionTab[];
}
